import { removeLineBreaksFromString } from 'module/helpers/html-sanitizer';
import { useLatest } from 'module/hooks/use-latest';
import { NoteField } from 'module/types/filter-field/field/filterable-field';
import { MultiLineTextField } from 'module/types/filter-field/field/filterable-field';
import { TextField } from 'module/types/filter-field/field/filterable-field';
import {
  isOptional,
  TextFieldFilter,
  TextFieldFilterOperationType,
  TextFieldFilterOperationTypes,
  TextFieldFilterOperationTypesWithValue,
} from 'module/types/filter-field/filter/text';
import { useDebounceEffect } from 'ahooks';
import { Form } from 'antd';
import { snakeCase } from 'lodash';
import { FC, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Select, TextInput } from './components';
import { CyWrapper } from './wrappers/cy-wrapper';

interface Props {
  filter: TextFieldFilter;
  onChange: (filter: TextFieldFilter) => void;
  getFilterOperationTypeOptions?: (
    field: TextField | MultiLineTextField | NoteField,
  ) => TextFieldFilter[];
  getOperationOptionRenderer?: (
    type: TextFieldFilterOperationType,
  ) => null | React.JSX.Element;
}

const defaultOptions = (
  field: TextField | MultiLineTextField | NoteField,
): TextFieldFilter[] =>
  (field.fieldName === 'projectName'
    ? TextFieldFilterOperationTypesWithValue
    : TextFieldFilterOperationTypes
  )
    .filter((x) => !field.metaData?.required || !isOptional(x))
    .map(
      (type) =>
        ({
          field,
          type,
        }) as any as TextFieldFilter,
    );

export const TextCriteria: FC<React.PropsWithChildren<Props>> = ({
  filter,
  onChange,
  getFilterOperationTypeOptions = defaultOptions,
  getOperationOptionRenderer,
}) => {
  const [text, setText] = useState<string | undefined>((filter as any).value);
  const filterText = (filter as any).value as string | undefined;
  const latestOnChange = useLatest(onChange);

  useEffect(() => {
    setText(filterText);
  }, [filterText, setText]);
  const filterRef = useRef(filter);

  useEffect(() => {
    filterRef.current = filter;
  }, [filterRef, filter]);
  useDebounceEffect(
    () => {
      latestOnChange({
        ...filterRef.current,
        value: text,
      } as any);
    },
    [filterRef, text, latestOnChange],
    {
      wait: 300,
      trailing: true,
    },
  );

  return (
    <>
      <Form.Item>
        <Select
          disabled={filter == null}
          value={filter?.type}
          onChange={(selected) =>
            onChange({
              ...filter,
              type: selected,
            } as any)
          }
        >
          {(filter?.field == null
            ? []
            : getFilterOperationTypeOptions(filter.field)
          ).map((option) => (
            <Select.Option key={option.type} value={option.type}>
              <CyWrapper filter={filter} option={option}>
                {getOperationOptionRenderer?.(option.type) ?? (
                  <FormattedMessage
                    id={`task_list.filters.${snakeCase(
                      TextFieldFilterOperationType[option.type],
                    )}`}
                  />
                )}
              </CyWrapper>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {filter != null &&
        (filter.type === TextFieldFilterOperationType.Contains ||
          filter.type === TextFieldFilterOperationType.DoesNotContain) && (
          <Form.Item>
            <TextInput
              data-cy={`select.fields.input.${
                filter?.field?.fieldLabel ?? '__new__'
              }`}
              value={text}
              onChange={(event) =>
                setText(removeLineBreaksFromString(event.target.value))
              }
            />
          </Form.Item>
        )}
    </>
  );
};
